import { ActionTree } from 'vuex';
import ApiUrl from '../../../api/url';
import { AutoLoginAppState, AutoLoginAppInput } from './types';
import { RootState } from '../../types';
import * as types from './mutation-types';
import { AppContext } from '../../context';
const MessageKey = AppContext.MessageKey;
import { translator } from '../../translator';
import { isHappenedOtherPartyError } from '../../error-type';
import router from '../../../router/index';
import ScreenUrl from '../../../router/url';

export const actions: ActionTree<AutoLoginAppState, RootState> = {
    /**
     * 自動ログインアプリ定義一覧取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} node_id 自動ログインアプリ定義と紐づくノードID
     * @param {any} ignore_node_id 除外ノードID
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @returns {Promise<void>}
     */
    async search(
        { dispatch, commit },
        { node_id = undefined, ignore_node_id = undefined, pageNumber, perPage, sort } = {}
    ) {
        const requestParams = {
            ignore_node_id: ignore_node_id,
            node_id: node_id,
            page_number: pageNumber,
            per_page: perPage,
            'sort[column]': sort.column,
            'sort[direction]': sort.direction,
        };
        try {
            commit(types.SEARCH_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.AutoLoginApp.INDEX,
                    params: requestParams,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SEARCH_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SEARCH_FAILURE, resData.errors[0]);
        }
    },

    /**
     * 自動ログインアプリ定義新規作成前のエラー等のクリア
     * 作成に失敗していた場合などに、そのままエラーメッセージなどVuex上に残っていたらクリアするため
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearError({ commit }) {
        commit(types.CLEAR_ERROR_AUTO_LOGIN_APP);
    },

    /**
     * 自動ログインアプリ定義新規作成
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {AutoLoginAppInput} autoLoginApp 新規情報
     * @returns {Promise<void>}
     */
    async create({ dispatch, commit, rootState }, autoLoginApp: AutoLoginAppInput) {
        try {
            commit(types.CREATE_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'post', url: ApiUrl.AutoLoginApp.CREATE, params: { auto_login_definitions: autoLoginApp } },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CREATE_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.AutoLoginApp.CREATED);
                dispatch('toastMessage', `${autoLoginApp.name} ${message}`, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.CREATE_FAILURE, params);
        }
    },

    /**
     * 自動ログインアプリ定義の詳細情報取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {number} id 自動ログインアプリ定義のID
     * @returns {Promise<void>}
     */
    async show({ dispatch, commit }, id: number) {
        try {
            commit(types.SHOW_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.AutoLoginApp.SHOW.replace(':id', id.toString()),
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SHOW_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SHOW_FAILURE, resData.errors[0]);
        }
    },

    /**
     * 自動ログインアプリ定義更新
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} editAutoLoginApp 更新する自動ログインアプリ定義情報
     * @param {any} autoLoginAppId 自動ログインアプリ定義ID
     * @returns {Promise<void>}
     */
    async update({ dispatch, commit, rootState }, { editAutoLoginApp, autoLoginAppId } = {}) {
        try {
            commit(types.UPDATE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'put',
                    url: ApiUrl.AutoLoginApp.UPDATE.replace(':id', autoLoginAppId.toString()),
                    params: { auto_login_definitions: editAutoLoginApp },
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.UPDATE_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.AutoLoginApp.UPDATED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.UPDATE_FAILURE, params);
        }
    },

    /**
     * 自動ログインアプリ定義削除
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {number} id 自動ログインアプリ定義ID
     * @returns {Promise<void>}
     */
    async destroy({ dispatch, commit, rootState }, id: number) {
        try {
            commit(types.DESTROY_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'delete',
                    url: ApiUrl.AutoLoginApp.DESTROY.replace(':id', id.toString()),
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.DESTROY_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.AutoLoginApp.DESTROYED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.DESTROY_FAILURE, params);
        }
    },

    /**
     * CSV出力
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @returns {Promise<void>}
     */
    async csv({ dispatch, commit, rootState }, { sort } = {}) {
        try {
            const payload = {
                sort: sort,
            };
            commit(types.CSV_OUTPUT_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'post', url: ApiUrl.AutoLoginApp.CSV_OUTPUT, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CSV_OUTPUT_SUCCESS, resData);
                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Csv.OUTPUT);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.CSV_OUTPUT_FAILURE, resData.errors[0]);
        }
    },

    /**
     * PAファイルダウンロード処理開始
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async paFileDownloadStart({ commit }) {
        commit(types.PA_FILE_DOWNLOAD_START);
    },

    /**
     * PAファイルダウンロード処理正常終了
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async paFileDownloadSuccess({ commit }) {
        commit(types.PA_FILE_DOWNLOAD_SUCCESS);
    },

    /**
     * PAファイルダウンロード処理エラー終了
     * @param {any} commit Vuex変数
     * @param resData エラーレスポンス
     * @returns {Promise<void>}
     */
    async paFileDownloadFailure({ commit }, resData) {
        const params = {
            errorTitle: resData.title,
            errorDetails: resData.errors,
        };
        commit(types.PA_FILE_DOWNLOAD_FAILURE, params);
    },

    /**
     * 画面切り替え時のstoreの消去
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearOnBeforeRoute({ commit }) {
        commit(types.CLEAR_ALL);
    },
};
